import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input, List, Modal, Tag, Typography } from 'antd'
import { SearchOutlined, UserOutlined } from '@ant-design/icons'
import { searchPoolUsers, PoolUserSearchItem } from '@/api/poolAdminApi'

const { Text } = Typography

export interface PalettePage {
  key: string
  label: string
  group: string
}

interface Props {
  open: boolean
  onClose: () => void
  pages: PalettePage[]
}

export const CommandPalette: FC<Props> = ({ open, onClose, pages }) => {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [users, setUsers] = useState<PoolUserSearchItem[]>([])
  const [searching, setSearching] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (open) {
      setQ('')
      setUsers([])
      setActiveIdx(0)
    }
  }, [open])

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    const kw = q.trim()
    if (!kw) {
      setUsers([])
      return
    }
    timerRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const r = await searchPoolUsers(kw, 5)
        setUsers(r.users || [])
      } catch {
        setUsers([])
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [q])

  const matchedPages = useMemo(() => {
    const kw = q.trim().toLowerCase()
    if (!kw) return pages
    return pages.filter(
      p => p.label.toLowerCase().includes(kw) || p.key.toLowerCase().includes(kw),
    )
  }, [pages, q])

  interface Row {
    kind: 'page' | 'user'
    key: string
    node: React.ReactNode
    go: () => void
  }

  const rows: Row[] = [
    ...matchedPages.map<Row>(p => ({
      kind: 'page',
      key: `page-${p.key}`,
      node: (
        <span>
          <Text strong>{p.label}</Text>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{p.group}</Text>
        </span>
      ),
      go: () => navigate(p.key),
    })),
    ...users.map<Row>(u => ({
      kind: 'user',
      key: `user-${u.user_id}`,
      node: (
        <span>
          <UserOutlined style={{ marginRight: 6, color: '#6366f1' }} />
          <Text strong>{u.nickname || u.user_id}</Text>
          {u.gender && <Tag style={{ marginLeft: 6 }} color="default">{u.gender}</Tag>}
          {u.city && <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>{u.city}</Text>}
          <Text type="secondary" code style={{ fontSize: 11, marginLeft: 8 }}>{u.user_id.slice(0, 12)}</Text>
        </span>
      ),
      go: () => navigate(`/pool/search?user_id=${encodeURIComponent(u.user_id)}`),
    })),
  ]

  const pick = (idx: number) => {
    const r = rows[idx]
    if (!r) return
    onClose()
    r.go()
  }

  useEffect(() => {
    setActiveIdx(0)
  }, [q])

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={560}
      styles={{ body: { padding: 0 } }}
      style={{ top: 120 }}
      destroyOnClose
    >
      <Input
        size="large"
        autoFocus
        prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
        placeholder="搜索页面或用户(昵称/ID)…"
        value={q}
        onChange={e => setQ(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActiveIdx(i => Math.min(i + 1, rows.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActiveIdx(i => Math.max(i - 1, 0))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            pick(activeIdx)
          }
        }}
        style={{ border: 'none', boxShadow: 'none', borderBottom: '1px solid #f0f0f0', borderRadius: 0 }}
      />
      <div style={{ maxHeight: 360, overflow: 'auto' }}>
        <List
          size="small"
          loading={searching}
          dataSource={rows}
          locale={{ emptyText: <div style={{ padding: 16 }}><Text type="secondary">无匹配结果</Text></div> }}
          renderItem={(r, idx) => (
            <List.Item
              style={{
                cursor: 'pointer',
                padding: '10px 16px',
                background: idx === activeIdx ? '#f5f5ff' : undefined,
              }}
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => pick(idx)}
            >
              {r.node}
            </List.Item>
          )}
        />
      </div>
      <div style={{ padding: '6px 16px', borderTop: '1px solid #f0f0f0', fontSize: 12, color: 'rgba(0,0,0,0.35)' }}>
        ↑↓ 移动 · Enter 打开 · Esc 关闭
      </div>
    </Modal>
  )
}
